import React from 'react'
import { Box, Button, Element, Heading } from 'react-bulma-components'
import { uniqBy } from 'lodash'
import { format, parseISO, startOfDay } from 'date-fns'
import { PayrollWorkerWithWeeksSummary } from '../../hooks/payroll'
import { WorkPeriodEvent } from '../../api/work-period-events'
import { calculateTotals } from './weeks-table/totals'
import { buildWorkPeriodEventLabel } from '../../utils/work-period-events'
import { getDocIdAsString } from '../../utils/documents'
import { sortByIgnoreCase } from '../../utils/arrays'
import { formatCompleteDate, isSameWeek } from '../../utils/date'

/** Single row: one event type and how many times it appears on displayed weeks */
interface PayrollEventsSummaryRow {
  event: WorkPeriodEvent
  count: number
}

/** Layout: label and count stay side-by-side (readable on wide cards). */
const SUMMARY_LINE_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: '0.35rem',
}

const LIST_STYLE: React.CSSProperties = { margin: 0, paddingLeft: '1.25rem' }

const DETAIL_LIST_STYLE: React.CSSProperties = {
  margin: '0.35rem 0 0 0',
  paddingLeft: '1.25rem',
  listStyleType: 'circle',
}

/** Row plus code string so sortByIgnoreCase can sort by event code. */
type PayrollEventsSummaryRowWithCode = PayrollEventsSummaryRow & { code: string }

/**
 * Resolves the week summary row for a calendar week (same matching as payroll totals).
 */
const findWeekSummaryForDisplayedWeek = (
  worker: PayrollWorkerWithWeeksSummary,
  week: Date,
): PayrollWorkerWithWeeksSummary['weeksSummary']['weeks'][0] | undefined => {
  return worker.weeksSummary?.weeks.find(
    w =>
      w.missions[0]?.workPeriods[0] &&
      isSameWeek(week, new Date(w.missions[0].workPeriods[0].start.date)),
  )
}

/**
 * Collects every occurrence date (work period start day) per work-period-event definition id,
 * limited to the displayed weeks interval.
 */
const collectOccurrenceDatesByEventId = (
  worker: PayrollWorkerWithWeeksSummary,
  weeks: Date[],
): Map<string, Date[]> => {
  const result = new Map<string, Date[]>()

  for (const week of weeks) {
    const weekSummary = findWeekSummaryForDisplayedWeek(worker, week)
    if (!weekSummary) continue

    for (const mission of weekSummary.missions) {
      for (const workPeriod of mission.workPeriods) {
        const dayStart = new Date(workPeriod.start.date)
        for (const we of workPeriod.events) {
          const eventDefId = getDocIdAsString(we.item)
          const list = result.get(eventDefId) ?? []
          list.push(dayStart)
          result.set(eventDefId, list)
        }
      }
    }
  }

  return result
}

/**
 * Groups raw occurrence timestamps by calendar day, sorted chronologically.
 */
const groupOccurrencesByCalendarDay = (dates: Date[]): Array<{ label: string; count: number }> => {
  if (dates.length === 0) return []
  const counts = new Map<string, number>()
  for (const d of dates) {
    const k = format(startOfDay(d), 'yyyy-MM-dd')
    counts.set(k, (counts.get(k) || 0) + 1)
  }
  return [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([dayKey, count]) => ({
      label: formatCompleteDate(parseISO(`${dayKey}T12:00:00`)),
      count,
    }))
}

/**
 * Filters by event type, maps to counts, drops zero counts, sorts by code (case/accent insensitive).
 */
const buildPayrollEventSummaryRows = (
  events: WorkPeriodEvent[],
  eventType: 'benefit' | 'informative',
  getCount: (event: WorkPeriodEvent) => number,
): PayrollEventsSummaryRow[] => {
  const withCode: PayrollEventsSummaryRowWithCode[] = events
    .filter(e => e.type === eventType)
    .map(event => ({
      event,
      count: getCount(event),
      code: event.code,
    }))
    .filter(r => r.count > 0)

  return sortByIgnoreCase(withCode, 'code').map(({ event, count }) => ({ event, count }))
}

interface PayrollEventsSummarySectionProps {
  title: string
  rows: PayrollEventsSummaryRow[]
  kind: 'benefit' | 'informative'
  /** Map event definition id → occurrence dates on displayed weeks */
  occurrenceDatesByEventId: Map<string, Date[]>
  /** Stable id for aria-controls / data-test section suffix */
  sectionId: string
  emptyMessage: string
}

/**
 * One section: title, optional link toggle, list of events with counts; expanded shows dates per event.
 */
const PayrollEventsSummarySection: React.FC<PayrollEventsSummarySectionProps> = ({
  title,
  rows,
  kind,
  occurrenceDatesByEventId,
  sectionId,
  emptyMessage,
}) => {
  const [detailsExpanded, setDetailsExpanded] = React.useState(false)
  const detailsIdPrefix = `payroll-events-summary-details-${sectionId}`
  const controlledIds = rows.map(r => `${detailsIdPrefix}-${getDocIdAsString(r.event)}`).join(' ')

  if (rows.length === 0)
    return (
      <Element textColor="grey" textSize={6}>
        {emptyMessage}
      </Element>
    )

  return (
    <Element>
      <Element display="flex" alignItems="center" flexWrap="wrap" mb={2} style={{ gap: '0.75rem' }}>
        <Element textWeight="semibold" textSize={6} mb={0}>
          {title}
        </Element>
        <Button
          type="button"
          text
          color="link"
          size="small"
          className="p-0"
          style={{ height: 'auto', textDecoration: 'underline' }}
          aria-expanded={detailsExpanded}
          aria-controls={detailsExpanded ? controlledIds : undefined}
          data-test={`payroll-events-summary-toggle-${sectionId}`}
          onClick={() => setDetailsExpanded(v => !v)}
        >
          {detailsExpanded ? 'Masquer les détails' : 'Afficher les détails'}
        </Button>
      </Element>
      <Element renderAs="ul" style={LIST_STYLE}>
        {rows.map(({ event, count }) => {
          const rowKey = kind === 'benefit' ? event.code : getDocIdAsString(event)
          const eventId = getDocIdAsString(event)
          const rawDates = occurrenceDatesByEventId.get(eventId) ?? []
          const dayGroups = groupOccurrencesByCalendarDay(rawDates)

          return (
            <Element
              renderAs="li"
              key={rowKey}
              data-test={`payroll-events-summary-${kind}-${event.code}`}
            >
              <Element renderAs="span" style={SUMMARY_LINE_STYLE}>
                <Element renderAs="span">{buildWorkPeriodEventLabel(event, true)}</Element>
                <Element renderAs="span" textColor="grey" textSize={6}>
                  × {count}
                </Element>
              </Element>
              {detailsExpanded && (
                <Element
                  renderAs="ul"
                  id={`${detailsIdPrefix}-${eventId}`}
                  style={DETAIL_LIST_STYLE}
                  data-test={`payroll-events-summary-dates-${kind}-${event.code}`}
                >
                  {dayGroups.length === 0 ? (
                    <Element renderAs="li">
                      <Element textColor="grey" textSize={7}>
                        Aucune date détaillée
                      </Element>
                    </Element>
                  ) : (
                    dayGroups.map(({ label, count: dayCount }) => (
                      <Element renderAs="li" key={`${event.code}-${label}`}>
                        <Element textSize={7}>
                          {dayCount > 1 ? `${label} (× ${dayCount})` : label}
                        </Element>
                      </Element>
                    ))
                  )}
                </Element>
              )}
            </Element>
          )
        })}
      </Element>
    </Element>
  )
}

export interface PayrollEventsSummaryProps {
  worker: PayrollWorkerWithWeeksSummary
  weeks: Date[]
  events: WorkPeriodEvent[]
}

/**
 * Lists work period events with counts on displayed weeks; each section can expand to show dates.
 */
const PayrollEventsSummary: React.FC<PayrollEventsSummaryProps> = ({ worker, weeks, events }) => {
  const employerRules = React.useMemo(
    () => uniqBy(worker.weeksSummary?.weeks.flatMap(w => w.employerRules) || [], 'ruleId'),
    [worker.weeksSummary?.weeks],
  )

  const { totalBenefitEvents, totalInformativeEventsByEvent } = React.useMemo(
    () => calculateTotals(weeks, worker, events, employerRules),
    [weeks, worker, events, employerRules],
  )

  const occurrenceDatesByEventId = React.useMemo(
    () => collectOccurrenceDatesByEventId(worker, weeks),
    [worker, weeks],
  )

  const benefitRows = React.useMemo(
    () =>
      buildPayrollEventSummaryRows(
        events,
        'benefit',
        e => totalBenefitEvents[`event_${e.code}`] || 0,
      ),
    [events, totalBenefitEvents],
  )

  const informativeRows = React.useMemo(
    () =>
      buildPayrollEventSummaryRows(
        events,
        'informative',
        e => totalInformativeEventsByEvent[getDocIdAsString(e)] || 0,
      ),
    [events, totalInformativeEventsByEvent],
  )

  const hasAny = benefitRows.length > 0 || informativeRows.length > 0

  if (weeks.length === 0) {
    return null
  }

  return (
    <Box mt={4} data-test="payroll-events-summary">
      <Heading size={6} mb={3}>
        Résumé des événements
      </Heading>
      {!hasAny ? (
        <Element textColor="grey" textSize={6}>
          Aucun événement prestation ou informatif sur cette période pour ce salarié.
        </Element>
      ) : (
        <Element display="flex" flexDirection="column" style={{ gap: '1rem' }}>
          <PayrollEventsSummarySection
            title="Événements prestation"
            rows={benefitRows}
            kind="benefit"
            occurrenceDatesByEventId={occurrenceDatesByEventId}
            sectionId="benefit"
            emptyMessage="Aucun événement prestation sur cette période pour ce salarié."
          />
          <PayrollEventsSummarySection
            title="Événements informatifs"
            rows={informativeRows}
            kind="informative"
            occurrenceDatesByEventId={occurrenceDatesByEventId}
            sectionId="informative"
            emptyMessage="Aucun événement informatif sur cette période pour ce salarié."
          />
        </Element>
      )}
    </Box>
  )
}

export default PayrollEventsSummary
